import Markdown from "markdown-to-jsx";
import React from "react";
import AgentChart from "./AgentChart";
import { AGENT_CHART_LANG, parseAgentChartSpec } from "./agentChartSpec";
import { toCurrentOrigin } from "./investigationLinks";

/**
 * The agent's answer, with its inline charts rendered.
 *
 * markdown-to-jsx turns a fenced block into `<pre><code class="...">`, so `pre`
 * is where a chart can be intercepted. `pre` rather than `code` deliberately: a
 * chart is a block, and rendering it from the `code` override would nest a chart
 * div inside a `<pre>` that the answer stylesheet gives a code background and
 * padding to.
 *
 * The language is matched against the class LIST, not the whole attribute.
 * markdown-to-jsx v9 emits BOTH `language-x` and `lang-x`, so an equality check
 * against `lang-x` silently never matches - which is exactly what shipped, and
 * meant every chart reached the operator as raw JSON while type-checking, lint
 * and the unit suite all passed.
 */
const isChartBlock = (className: unknown): boolean =>
  typeof className === "string" &&
  className.split(/\s+/).includes(`lang-${AGENT_CHART_LANG}`);

const PreBlock = ({ children, ...rest }: { children?: React.ReactNode }) => {
  const child = React.Children.toArray(children)[0];
  if (React.isValidElement(child)) {
    const { className, children: code } = child.props as {
      className?: string;
      children?: React.ReactNode;
    };
    if (isChartBlock(className)) {
      const spec = parseAgentChartSpec(String(code ?? ""));
      if (spec) return <AgentChart spec={spec} />;
    }
  }
  return <pre {...rest}>{children}</pre>;
};

/** An admin link, pointed at the environment the operator is actually in. */
const AnswerLink = ({
  href,
  children,
  ...rest
}: {
  href?: string;
  children?: React.ReactNode;
}) => (
  <a
    {...rest}
    href={href ? toCurrentOrigin(href, window.location.origin) : href}
  >
    {children}
  </a>
);

const InvestigationMarkdown = ({ children }: { children: string }) => (
  <Markdown
    options={{
      overrides: { pre: { component: PreBlock }, a: { component: AnswerLink } },
    }}
  >
    {children}
  </Markdown>
);

export default InvestigationMarkdown;
